import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "stb_cart_v1";

const lineKey = (id, size, color, custom) => `${id}::${size || ""}::${color || ""}::${custom || ""}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, size = null, qty = 1, color = null, customization = null, bulkLbs = null) => {
    setItems((prev) => {
      const key = lineKey(product.id, size, color, customization);
      const existing = prev.find((i) => lineKey(i.product_id, i.size, i.color, i.customization) === key);
      if (existing) {
        return prev.map((i) =>
          lineKey(i.product_id, i.size, i.color, i.customization) === key ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          size,
          color,
          customization,
          bulk_lbs: bulkLbs,
          quantity: qty,
        },
      ];
    });
    setOpen(true);
  };

  const updateQty = (product_id, size, color, custom, qty) => {
    if (qty <= 0) return removeItem(product_id, size, color, custom);
    setItems((prev) =>
      prev.map((i) =>
        lineKey(i.product_id, i.size, i.color, i.customization) === lineKey(product_id, size, color, custom) ? { ...i, quantity: qty } : i
      )
    );
  };

  const removeItem = (product_id, size, color, custom) => {
    setItems((prev) =>
      prev.filter((i) => lineKey(i.product_id, i.size, i.color, i.customization) !== lineKey(product_id, size, color, custom))
    );
  };

  const clear = () => setItems([]);

  const total = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQty, removeItem, clear, total, count, open, setOpen }}
    >
      {children}
    </CartContext.Provider>
  );
};
